import React, { useState, useEffect } from "react";
import { Button, Checkbox, Spin } from "antd";
import { saveUserPreferences } from "../service";
import {
  getAsset,
  getLabel,
  shouldShowSponsorship,
} from "../../../utils/appConfig";
import "./TatvaShots.css";
import { useNavigate } from "react-router-dom";
import useCategories from "../../../hooks/useCategories";
import useStore from "../../../store/useStore";

interface CategorySelectionProps {
  preferences: number[];
  setPreferences: Function;
}

const CategorySelection: React.FC<CategorySelectionProps> = ({
  preferences,
  setPreferences,
}: any) => {
  const [loader, setLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const { getCategories } = useCategories();
  const { tatvaShots, planDetails } = useStore();
  const { categories } = tatvaShots;
  const { shot_plan, isSelfShotsPlan } = planDetails || {};
  const { service_payer } = shot_plan || {};
  const [catLoader, setCatLoader] = useState<boolean>(false);
  // Get the appropriate logo based on app configuration
  const shotsLogo = getAsset("shots");
  const shotsLabel = getLabel("shotsLabel");
  // Check if we should show sponsorship information
  const showSponsorship = shouldShowSponsorship();

  // Fetch categories from API
  useEffect(() => {
    setUserPreferences();
  }, []);

  const setUserPreferences = async () => {
    setCatLoader(true);
    const categories = await getCategories();
    setCatLoader(false);
    if (!preferences?.length) {
      setPreferences(categories);
    }
  };

  const handleCheckboxChange = (category: any) => {
    setPreferences((prevPreferences: any) =>
      !!prevPreferences?.find((pref: any) => pref?.id === category?.id)
        ? prevPreferences.filter((pref: any) => pref?.id !== category?.id)
        : [...prevPreferences, { ...category, id: category?.id }]
    );
  };

  const handleGetStarted = async () => {
    try {
      setLoader(true);
      const selected = tatvaShots?.categories.filter(
        (category: any) =>
          !!preferences?.find((pref: any) => pref?.id === category?.id)
      );

      // Fetch speciality tags concurrently for each selected category
      const specialityTags = selected.map((category) => {
        return { id: category.id };
      });

      const payload = { prefs: specialityTags };

      const res = await saveUserPreferences(payload);
      if (res?.status === 200) {
        navigate("/tatva-shots-home");
      }
    } catch (error) {
      console.error("Error in getting started:", error);
    } finally {
      setLoader(false);
    }
  };

  const isAllSelected = () => {
    return tatvaShots?.categories?.length === preferences?.length;
  };

  const handleSelectAllToggle = () => {
    if (isAllSelected()) {
      setPreferences([]);
    } else {
      setPreferences(tatvaShots?.categories);
    }
  };

  return (
    <div className="flex flex-col h-screen w-full">
      {/* Sticky Header */}
      <div className="sticky top-0 bg-white p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex flex-col items-center">
            <img
              src={shotsLogo}
              alt={shotsLabel}
              className="h-[38px]"
              loading="lazy"
            />
            {showSponsorship && !isSelfShotsPlan && service_payer?.containerName && (
              <div className="flex items-center text-center font-normal leading-[17px] text-[8px]">
                <div className="self-stretch my-auto">Sponsored By</div>
                <img
                  loading="lazy"
                  src={service_payer?.containerName}
                  className="object-contain shrink-0 self-stretch my-auto aspect-[2.44] w-[33px]"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Category List */}
      <div className="flex-1 overflow-y-auto p-4 text-left">
        <h2 className="text-[20px] font-semibold leading-[28px]">
          Personalize your feed
        </h2>
        <p className="text-[14px] font-normal leading-[22px] text-[#3D4854]">
          You'll see based on your selected specialties and personalize your
          feed anytime.
        </p>
        <div className="flex items-center mb-2">
          <button
            onClick={handleSelectAllToggle}
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium underline mt-2"
          >
            {isAllSelected() ? "Unselect All" : "Select All"}
          </button>
        </div>

        {catLoader ? (
          <Spin
            size="large"
            className="flex justify-center my-4 items-center"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 mt-4 my-20 text-left">
            {categories?.map((category) => (
              <div
                key={category.id}
                className={`p-4 border ${!!preferences?.find((pref: any) => pref?.id === category?.id)
                  ? "border-[#3D8C40] bg-[#EDF7ED]"
                  : "border-[#DCE4E8]"
                  } rounded-2xl flex gap-x-2 items-baseline w-[calc(50%-1rem)] justify-between cursor-pointer`}
                style={{ width: "100%" }}
                onClick={() => handleCheckboxChange(category)}
              >
                <div className="flex">
                  <div
                    className={`font-semibold ${!!preferences?.find(
                      (pref: any) => pref?.id === category?.id
                    )
                      ? "text-[#3D8C40]"
                      : "text-[#5A6774]"
                      }`}
                  >
                    {category.displayName}
                  </div>
                </div>
                <Checkbox
                  className="category-check-box"
                  checked={
                    !!preferences?.find(
                      (pref: any) => pref?.id === category?.id
                    )
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 w-full bg-white shadow-lg p-4 z-20 max-w-[1280px] mx-auto">
        <Button
          type="primary"
          className="w-full bg-[#4B4AD5] p-[16px] h-[58px] rounded-lg text-[18px] font-medium leading-[26px]"
          onClick={handleGetStarted}
          loading={loader}
          disabled={loader || !preferences?.length}
        >
          Get Started
        </Button>
      </div>
    </div>
  );
};

export default CategorySelection;
